import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    InputBase,
    Switch,
    Slider,
    Tooltip,
    IconButton,
    Chip,
    CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import GavelIcon from '@mui/icons-material/Gavel';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import { PanelHeader, SectionCard, RangeBtn } from './shared';
import { GanttChart } from './charts/GanttChart';
import { EventCard } from './EventCard';
import { formatVolume } from '../utils/format';
import { useEventSearch } from '../api/useUnifiedSearch';
import type { EventFromAsset } from '../api/types';
import { useRelatedEvents } from '../api/useRelatedEvents';
import type { RelatedEventsResponse } from '../api/useRelatedEvents';
import HubIcon from '@mui/icons-material/Hub';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ── Market type config ──

type MarketType =
    | 'price_level'
    | 'macro_release'
    | 'policy_decision'
    | 'geopolitical'
    | 'corporate';

interface LaneConfig {
    key: MarketType;
    label: string;
    color: string;
}

const MARKET_TYPE_CONFIG: LaneConfig[] = [
    { key: 'price_level', label: 'Price Level', color: '#f59e0b' },
    { key: 'macro_release', label: 'Macro Release', color: '#3b82f6' },
    { key: 'policy_decision', label: 'Policy Decision', color: '#a855f7' },
    { key: 'geopolitical', label: 'Geopolitical', color: '#ef4444' },
    { key: 'corporate', label: 'Corporate', color: '#10b981' },
];

const MUI_ICONS: Record<MarketType, typeof TrendingUpIcon> = {
    price_level: TrendingUpIcon,
    macro_release: BarChartIcon,
    policy_decision: GavelIcon,
    geopolitical: PublicIcon,
    corporate: BusinessIcon,
};

type DisplayMode = 'both' | 'top_only' | 'bottom_only';
type SlotComponent = 'gantt' | 'cards';

const INITIAL_CARD_COUNT = 6;
const DAY_MS = 86400000;
const EMPTY_EVENTS: EventFromAsset[] = [];
type TimeRangeKey = '3M' | '6M' | '1Y' | 'ALL';

const TIME_RANGE_OPTIONS: TimeRangeKey[] = ['3M', '6M', '1Y', 'ALL'];

const TIME_RANGE_DAYS: Record<TimeRangeKey, number> = {
    '3M': 90,
    '6M': 180,
    '1Y': 365,
    'ALL': 365 * 3,
};

const MAX_EVENTS_FOR_AUTO_RANGE = 25;

// ── Props ──

export interface EventsPanelProps {
    timeDomain?: [Date, Date];
    maxFutureMs?: number;
    hoveredEvent: EventFromAsset | null;
    onHoverChange: (e: EventFromAsset | null) => void;
    selectedEvent: EventFromAsset | null;
    onSelectEvent: (e: EventFromAsset) => void;
    display?: DisplayMode;
    topComponent?: SlotComponent;
    bottomComponent?: SlotComponent;
    topMinHeight?: number;
    bottomMinHeight?: number;
    chartMarginLeft?: number;
    onZoomRangeChange?: (range: [Date, Date] | null) => void;
    assetContext?: { ticker: string; assetType: string; name: string } | null;
    futureOverhangPx?: number;
    onEventsChange?: (events: EventFromAsset[]) => void;
    eventContext?: string | null;        // event identifier → related events mode
    onTimeRangeChange?: (range: [Date, Date]) => void;  // for AssetView linking
    fillHeight?: boolean;                // expand to fill parent height instead of using fixed slot heights
}

export function EventsPanel({
    timeDomain: timeDomainProp,
    maxFutureMs,
    hoveredEvent,
    onHoverChange,
    selectedEvent,
    onSelectEvent,
    display: displayProp = 'both',
    topComponent = 'gantt',
    bottomComponent = 'cards',
    topMinHeight = 250,
    bottomMinHeight = 200,
    chartMarginLeft,
    onZoomRangeChange,
    assetContext,
    futureOverhangPx = 0,
    onEventsChange,
    eventContext = null,
    onTimeRangeChange,
    fillHeight = false,
}: EventsPanelProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // ── Internal filter state ──
    const [display, setDisplay] = useState<DisplayMode>(displayProp);
    const [searchText, setSearchText] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [activeOnly, setActiveOnly] = useState(false);
    const [volumeRange, setVolumeRange] = useState<[number, number]>([0, 100]);
    const [selectedTypes, setSelectedTypes] = useState<Set<MarketType> | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showAllCards, setShowAllCards] = useState(false);
    const [minVolume, setMinVolume] = useState(0);

    const [relatedEventId, setRelatedEventId] = useState<string | null>(null);
    const [relatedSourceEvent, setRelatedSourceEvent] = useState<EventFromAsset | null>(null);
    const cardsSectionRef = useRef<HTMLDivElement>(null);
    const activeEventContext = relatedEventId ?? eventContext;
    const isRelatedMode = activeEventContext !== null;
    const isNavigatedAway = relatedEventId !== null && relatedEventId !== eventContext;

    const [relatedTab, setRelatedTab] = useState<'series' | 'similar'>('series');

    const {
        data: relatedData,
        loading: relatedLoading,
        error: relatedError,
    } = useRelatedEvents(activeEventContext);

    // Auto-switch to similar tab when no series events
    useEffect(() => {
        if (relatedData && relatedData.series_events.length === 0) {
            setRelatedTab('similar');
        }
    }, [relatedData]);

    // ── Time range control (used when timeDomainProp is not provided) ──
    const hasExternalTimeDomain = timeDomainProp !== undefined;
    const [timeRangeKey, setTimeRangeKey] = useState<TimeRangeKey>('6M');
    const timeRangeAutoSet = useRef(false);

    // ── Data fetching ──
    const searchAssetContext = useMemo(
        () => assetContext ? { ticker: assetContext.ticker, assetType: assetContext.assetType } : null,
        [assetContext?.ticker, assetContext?.assetType],
    );

    const {
        activeEvents: searchActiveEvents,
        closedEvents: searchClosedEvents,
        loading: searchLoading,
        error: searchError,
    } = useEventSearch(searchText, isRelatedMode ? null : searchAssetContext);

    const activeEvents = isRelatedMode
        ? (relatedTab === 'series' ? (relatedData?.series_events ?? EMPTY_EVENTS) : (relatedData?.similar_events ?? EMPTY_EVENTS))
        : searchActiveEvents;
    const closedEvents = isRelatedMode ? EMPTY_EVENTS : searchClosedEvents;
    const loading = isRelatedMode ? relatedLoading : searchLoading;
    const error = isRelatedMode ? relatedError : searchError;

    const events = useMemo(() => {
        const base = [...activeEvents, ...closedEvents];
        if (isRelatedMode && relatedSourceEvent && !base.some(e => e.event_id === relatedSourceEvent.event_id)) {
            return [relatedSourceEvent, ...base];
        }
        return base;
    }, [activeEvents, closedEvents, isRelatedMode, relatedSourceEvent]);

    // ── Log-scale volume helpers ──
    const maxEventVolume = useMemo(
        () => Math.max(...events.map(e => e.volume || 0), 1000),
        [events],
    );

    const logToVolume = useCallback((pos: number): number => {
        if (pos <= 0) return 0;
        if (pos >= 100) return Infinity;
        const minLog = Math.log10(1000);
        const maxLog = Math.log10(Math.max(maxEventVolume, 10_000));
        return Math.pow(10, minLog + (pos / 100) * (maxLog - minLog));
    }, [maxEventVolume]);

    const volumeLabel = useCallback((pos: number): string => {
        if (pos <= 0) return '$0';
        if (pos >= 100) return '∞';
        return formatVolume(logToVolume(pos));
    }, [logToVolume]);

    useEffect(() => {
        setVolumeRange([0, 100]);
    }, [maxEventVolume]);

    useEffect(() => {
        onEventsChange?.(events);
    }, [events, onEventsChange]);

    const handleTypeClick = useCallback((key: MarketType) => {
        setSelectedTypes(prev => {
            if (prev === null) {
                const next = new Set(MARKET_TYPE_CONFIG.map(c => c.key));
                next.delete(key);
                return next;
            }
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                if (next.size === 0) return null;
                return next;
            }
            next.add(key);
            if (next.size === MARKET_TYPE_CONFIG.length) return null;
            return next;
        });
    }, []);

    const handleLaneIsolate = useCallback((key: MarketType) => {
        setSelectedTypes(prev => {
            if (prev !== null && prev.size === 1 && prev.has(key)) return null;
            return new Set([key]);
        });
    }, []);

    const handleLoadRelated = useCallback((evt: EventFromAsset) => {
        setRelatedEventId(evt.slug || evt.event_id);
        setRelatedSourceEvent(evt);
        setRelatedTab('series');
        setShowAllCards(false);
        timeRangeAutoSet.current = false;
        setTimeout(() => {
            cardsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    }, []);

    const handleBackToOriginal = useCallback(() => {
        setRelatedEventId(null);
        setRelatedSourceEvent(null);
        setRelatedTab('series');
        setShowAllCards(false);
        timeRangeAutoSet.current = false;
    }, []);

    const isTypeVisible = useCallback(
        (key: MarketType) => selectedTypes === null || selectedTypes.has(key),
        [selectedTypes],
    );

    // Search splits active/closed on the closed flag. Test membership in the
    // active list: text search returns an event with some closed markets in
    // both lists. Related events carry no flag, so that mode still falls back to
    // end_date, which can hide events that trade past their estimated end.
    const activeEventIds = useMemo(
        () => new Set(activeEvents.map(e => e.event_id)),
        [activeEvents],
    );
    const isEnded = useCallback(
        (e: EventFromAsset, now: Date) =>
            isRelatedMode ? new Date(e.end_date) < now : !activeEventIds.has(e.event_id),
        [isRelatedMode, activeEventIds],
    );

    // ── Filtered events (client-side filters only, search is handled by API) ──
    const filteredEvents = useMemo(() => {
        const now = new Date();
        const minVol = logToVolume(volumeRange[0]);
        const maxVol = logToVolume(volumeRange[1]);
        return events.filter(e => {
            if (isRelatedMode && relatedSourceEvent?.event_id === e.event_id) return true;
            if (activeOnly && isEnded(e, now)) return false;
            if (!isTypeVisible(e.market_type as MarketType)) return false;
            const vol = e.volume || 0;
            if (minVol > 0 && vol < minVol) return false;
            if (maxVol < Infinity && vol > maxVol) return false;
            return true;
        });
    }, [events, activeOnly, isEnded, isTypeVisible, volumeRange, logToVolume, isRelatedMode, relatedSourceEvent]);

    const visibleEvents = useMemo(() => {
        const isSingleType = selectedTypes !== null && selectedTypes.size === 1;
        const hasActiveSearch = searchText.length > 0;
        const hasVolumeFilter = minVolume > 0;

        // In related mode, show all events (they're already curated by the API)
        if (isRelatedMode || isSingleType || hasActiveSearch || hasVolumeFilter) {
            return filteredEvents;
        }
        // Default overview: top 1 per market_type by volume (AssetView / DiscoverView)
        const bestPerType = new Map<string, EventFromAsset>();
        for (const e of filteredEvents) {
            const existing = bestPerType.get(e.market_type);
            if (!existing || (e.volume ?? 0) > (existing.volume ?? 0)) {
                bestPerType.set(e.market_type, e);
            }
        }
        return [...bestPerType.values()];
    }, [filteredEvents, selectedTypes, searchText, minVolume, isRelatedMode]);

    // Auto-pick time range to show ≤25 events when related data loads
    useEffect(() => {
        if (hasExternalTimeDomain || !isRelatedMode || !relatedData) return;
        if (timeRangeAutoSet.current && relatedEventId === null) return;

        const evts = relatedTab === 'series'
            ? relatedData.series_events
            : relatedData.similar_events;

        if (evts.length <= MAX_EVENTS_FOR_AUTO_RANGE) {
            setTimeRangeKey('6M');
        } else {
            const now = Date.now();
            for (const key of TIME_RANGE_OPTIONS) {
                const cutoff = now - TIME_RANGE_DAYS[key] * DAY_MS;
                const count = evts.filter(e => new Date(e.end_date).getTime() >= cutoff).length;
                if (count <= MAX_EVENTS_FOR_AUTO_RANGE || key === 'ALL') {
                    setTimeRangeKey(key);
                    break;
                }
            }
        }
        timeRangeAutoSet.current = true;
    }, [relatedData, relatedTab, isRelatedMode, hasExternalTimeDomain, relatedEventId]);

    // Reset auto-set flag when navigating to a different event
    useEffect(() => {
        timeRangeAutoSet.current = false;
    }, [activeEventContext]);

    const timeDomain = useMemo((): [Date, Date] => {
        if (timeDomainProp) return timeDomainProp;

        const now = new Date();
        const spanMs = TIME_RANGE_DAYS[timeRangeKey] * DAY_MS;
        const start = new Date(now.getTime() - spanMs);

        if (visibleEvents.length === 0) {
            return [start, new Date(now.getTime() + 30 * DAY_MS)];
        }

        // Use the selected time range as the base domain,
        // only extend the end slightly for padding
        const rangeStart = start.getTime();
        let endT = now.getTime();

        // Extend end to cover events that end in the future (within reason)
        for (const e of visibleEvents) {
            const end = new Date(e.end_date).getTime();
            if (end > endT) endT = end;
        }

        const pad = Math.max((endT - rangeStart) * 0.03, 7 * DAY_MS);
        endT += pad;
        if (maxFutureMs != null) {
            const cap = Date.now() + maxFutureMs;
            if (endT > cap) endT = cap;
        }
        return [new Date(rangeStart - pad), new Date(endT)];
    }, [timeDomainProp, timeRangeKey, visibleEvents, maxFutureMs]);

    const sortedForCards = useMemo(() => {
        const sorted = [...visibleEvents].sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
        if (isRelatedMode && relatedSourceEvent) {
            const filtered = sorted.filter(e => e.event_id !== relatedSourceEvent.event_id);
            return [relatedSourceEvent, ...filtered];
        }
        return sorted;
    }, [visibleEvents, isRelatedMode, relatedSourceEvent]);

    const visibleCards = showAllCards ? sortedForCards : sortedForCards.slice(0, INITIAL_CARD_COUNT);
    const hasMoreCards = sortedForCards.length > INITIAL_CARD_COUNT;

    const showTop = display !== 'bottom_only';
    const showBottom = display !== 'top_only';
    const isEmpty = visibleEvents.length === 0 && !loading;

    const cycleDisplay = useCallback(() => {
        setDisplay(prev => {
            if (prev === 'both') return 'top_only';
            if (prev === 'top_only') return 'bottom_only';
            return 'both';
        });
    }, []);

    const renderSlot = (component: SlotComponent, minHeight: number) => {
        if (component === 'gantt') {
            const slotSx = fillHeight
                ? {
                      flex: 1,
                      minHeight: isEmpty ? 60 : minHeight,
                      display: 'flex',
                      flexDirection: 'column' as const,
                  }
                : { minHeight: isEmpty ? 60 : minHeight, height: minHeight };
            return (
                <Box sx={slotSx}>
                    {loading ? (
                        <Skeleton
                            variant="rounded"
                            height={fillHeight ? undefined : minHeight}
                            sx={{
                                mx: 1,
                                my: 1,
                                flex: fillHeight ? 1 : undefined,
                            }}
                        />
                    ) : isEmpty ? (
                        <EmptyState />
                    ) : (
                        <Box
                            sx={
                                fillHeight
                                    ? {
                                          flex: 1,
                                          minHeight: 0,
                                          display: 'flex',
                                          flexDirection: 'column',
                                      }
                                    : undefined
                            }
                        >
                            <GanttChart
                                events={visibleEvents}
                                timeDomain={timeDomain}
                                hoveredEvent={hoveredEvent}
                                onHover={onHoverChange}
                                onClick={onSelectEvent}
                                selectedEvent={selectedEvent}
                                sourceEventId={relatedSourceEvent?.event_id ?? null}
                                chartMarginLeft={chartMarginLeft}
                                onZoomRangeChange={onZoomRangeChange}
                                onLaneClick={handleLaneIsolate}
                                futureOverhangPx={futureOverhangPx}
                            />
                        </Box>
                    )}
                </Box>
            );
        }

        return (
            <Box sx={{ minHeight: isEmpty ? 60 : minHeight, px: 1.5, py: 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height={80} variant="rounded" />
                        ))}
                    </Box>
                ) : isEmpty ? (
                    <EmptyState />
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    lg: '1fr 1fr 1fr',
                                },
                                gap: 1,
                            }}
                        >
                            {visibleCards.map(e => {
                                const isSource = isRelatedMode && relatedSourceEvent?.event_id === e.event_id;
                                return (
                                    <EventCard
                                        key={e.event_id}
                                        event={e}
                                        isHovered={hoveredEvent?.event_id === e.event_id}
                                        onHover={onHoverChange}
                                        assetContext={assetContext ?? undefined}
                                        onLoadRelated={isSource || eventContext ? undefined : handleLoadRelated}
                                        isSource={isSource}
                                        onResetRelated={isSource ? handleBackToOriginal : undefined}
                                    />
                                );
                            })}
                        </Box>
                        {hasMoreCards && (
                            <Typography
                                onClick={() => setShowAllCards(v => !v)}
                                sx={{
                                    mt: 1,
                                    fontSize: '12px',
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                }}
                            >
                                {showAllCards
                                    ? 'Show less'
                                    : `Show all ${sortedForCards.length} events`}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        );
    };

    const displayIcon = display === 'both'
        ? <ViewAgendaIcon sx={{ fontSize: 16 }} />
        : <ViewStreamIcon sx={{ fontSize: 16 }} />;

    const displayTooltip = display === 'both'
        ? 'Showing both views'
        : display === 'top_only'
            ? `Showing ${topComponent} only`
            : `Showing ${bottomComponent} only`;

    const searchBorderColor = searchFocused ? theme.palette.primary.main : 'transparent';

    // No series siblings and no embedding-based similars → nothing to show; errors still render.
    const relatedHasContent =
        relatedSourceEvent !== null ||
        (!relatedLoading &&
            relatedData !== null &&
            (relatedData.series_events.length > 0 ||
                relatedData.similar_events.length > 0));

    if (isRelatedMode && !relatedError && !relatedHasContent) {
        // Resolved with nothing to show — hide. Only an in-flight request holds
        // a compact spinner, so a lookup that never runs can't latch it.
        if (!relatedLoading) return null;
        return (
            <SectionCard>
                <PanelHeader title="Events" icon="◫" />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        py: 2.5,
                    }}
                >
                    <CircularProgress size={14} thickness={5} />
                    <Typography
                        sx={{ fontSize: '13px', color: 'text.disabled' }}
                    >
                        Loading events…
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    return (
        <div
            ref={cardsSectionRef}
            style={{
                scrollMarginTop: 64,
                ...(fillHeight
                    ? {
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: 0,
                      }
                    : {}),
            }}
        >
            <SectionCard
                sx={
                    fillHeight
                        ? {
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: 0,
                              flex: 1,
                          }
                        : undefined
                }
            >
                <PanelHeader title="Events" icon="◫">
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1, flex: 1, ml: 2, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1 }}>

                            {/* Back button when navigated away */}
                            {isNavigatedAway && (
                                <Tooltip title="Back to original">
                                    <IconButton size="small" onClick={handleBackToOriginal} sx={{ p: 0.5 }}>
                                        <ArrowBackIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {/* Series / Similar tabs in related mode */}
                            {isRelatedMode && relatedData && (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {relatedData.series_events.length > 0 && (
                                        <Chip
                                            label={relatedData.series_title ? `Series - ${relatedData.series_title}` : 'Series'}
                                            size="small"
                                            variant={relatedTab === 'series' ? 'filled' : 'outlined'}
                                            onClick={() => { setRelatedTab('series'); setShowAllCards(false); }}
                                            sx={{ fontSize: '11px', height: 22 }}
                                        />
                                    )}
                                    <Chip
                                        label="Similar"
                                        size="small"
                                        variant={relatedTab === 'similar' ? 'filled' : 'outlined'}
                                        onClick={() => { setRelatedTab('similar'); setShowAllCards(false); }}
                                        sx={{ fontSize: '11px', height: 22 }}
                                    />
                                </Box>
                            )}

                            {/* Time range control — only when no external timeDomain */}
                            {!hasExternalTimeDomain && (
                                <RangeBtn
                                    options={TIME_RANGE_OPTIONS as unknown as string[]}
                                    active={timeRangeKey}
                                    onChange={(v) => setTimeRangeKey(v as TimeRangeKey)}
                                />
                            )}

                            {/* Search input — hide in related mode */}
                            {!isRelatedMode && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        py: '3px',
                                        bgcolor: isDark
                                            ? alpha('#fff', 0.06)
                                            : alpha('#000', 0.04),
                                        border: 1,
                                        borderColor: searchBorderColor,
                                        borderRadius: '6px',
                                        transition: 'border-color 0.15s',
                                        minWidth: 120,
                                        maxWidth: 220,
                                        flex: '1 1 120px',
                                    }}
                                >
                                    <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                                    <InputBase
                                        placeholder="Search events…"
                                        value={searchText}
                                        onChange={e => setSearchText(e.target.value)}
                                        onFocus={() => setSearchFocused(true)}
                                        onBlur={() => setSearchFocused(false)}
                                        sx={{
                                            fontSize: '12px',
                                            flex: 1,
                                            '& input': { p: 0 },
                                            '& input::placeholder': {
                                                color: 'text.disabled',
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                    {searchText && (
                                        <CloseIcon
                                            onClick={() => setSearchText('')}
                                            sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer' }}
                                        />
                                    )}
                                </Box>
                            )}
                            {/* Active only toggle */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Switch
                                    size="small"
                                    checked={activeOnly}
                                    onChange={(_, v) => setActiveOnly(v)}
                                    sx={{
                                        width: 32,
                                        height: 18,
                                        p: 0,
                                        '& .MuiSwitch-switchBase': {
                                            p: '2px',
                                            '&.Mui-checked': {
                                                transform: 'translateX(14px)',
                                                '& + .MuiSwitch-track': {
                                                    bgcolor: 'primary.main',
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                        '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                        '& .MuiSwitch-track': {
                                            borderRadius: 9,
                                            bgcolor: isDark ? alpha('#fff', 0.15) : alpha('#000', 0.12),
                                        },
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: activeOnly ? 'text.primary' : 'text.disabled',
                                        whiteSpace: 'nowrap',
                                        userSelect: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setActiveOnly(v => !v)}
                                >
                                    Active only
                                </Typography>
                            </Box>

                            <Typography
                                sx={{ fontSize: '10px', color: 'text.disabled', fontFamily: 'monospace' }}
                            >
                                {filteredEvents.length} events
                            </Typography>
                        </Box>

                        {/* Right: type chips + filter + display toggle */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            {MARKET_TYPE_CONFIG.map(cfg => {
                                const Icon = MUI_ICONS[cfg.key];
                                const visible = isTypeVisible(cfg.key);
                                return (
                                    <Tooltip key={cfg.key} title={cfg.label}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleTypeClick(cfg.key)}
                                            sx={{
                                                p: 0.5,
                                                border: 1,
                                                borderColor: visible ? cfg.color : 'transparent',
                                                borderRadius: '6px',
                                                color: visible ? cfg.color : 'text.disabled',
                                            }}
                                        >
                                            <Icon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                );
                            })}
                            <Tooltip title="Volume filter">
                                <IconButton
                                    size="small"
                                    onClick={() => setShowFilters(v => !v)}
                                    sx={{
                                        color: showFilters ? 'primary.main' : 'text.disabled',
                                        p: 0.5,
                                    }}
                                >
                                    <FilterListIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={displayTooltip}>
                                <IconButton size="small" onClick={cycleDisplay} sx={{ p: 0.5, color: 'text.secondary' }}>
                                    {displayIcon}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </PanelHeader>

                {showFilters && (
                    <Box
                        sx={{
                            px: 1.5, py: 1,
                            display: 'flex', alignItems: 'center', gap: 2,
                            borderBottom: 1, borderColor: 'divider',
                        }}
                    >
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            Vol:
                        </Typography>
                        <Slider
                            size="small"
                            value={volumeRange}
                            onChange={(_, v) => setVolumeRange(v as [number, number])}
                            min={0}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={volumeLabel}
                            sx={{ flex: 1, maxWidth: 200, ml: 0.5 }}
                        />
                    </Box>
                )}

                {error && (
                    // A failed related lookup usually just means nothing relates to this
                    // event; a failed search is a real fault and still says so.
                    <Typography sx={{ color: isRelatedMode ? 'text.disabled' : 'error.main', fontSize: '13px', textAlign: 'center', py: 2 }}>
                        {isRelatedMode ? 'No events found' : 'Failed to load events'}
                    </Typography>
                )}

                {!error && showTop && renderSlot(topComponent, topMinHeight)}
                {!error && showTop && showBottom && (
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                )}
                {!error && showBottom && renderSlot(bottomComponent, bottomMinHeight)}

                {!loading && !error && (
                    <Typography
                        sx={{ py: 0.75, fontSize: '11px', color: 'text.disabled', textAlign: 'center' }}
                    >
                        {visibleEvents.length} / {filteredEvents.length} {activeOnly ? 'active ' : ''}events
                        {selectedTypes !== null && selectedTypes.size === 1 && (
                            <Box
                                component="span"
                                onClick={() => setSelectedTypes(null)}
                                sx={{ ml: 1, color: 'primary.main', cursor: 'pointer' }}
                            >
                                Show all categories
                            </Box>
                        )}
                    </Typography>
                )}
            </SectionCard>
        </div>
    );
}

function EmptyState() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 60 }}>
            <Typography sx={{ color: 'text.disabled', fontSize: '13px' }}>
                No events match current filters
            </Typography>
        </Box>
    );
}